// src/components/LockScreen.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Lock, Eye, EyeOff, Key, ArrowRight } from 'lucide-react';
import { useAuth } from './AuthContext';
import fedisLogo from '../assets/fedis.png';

/**
 * Fond WebGL : brouillard atmospherique lent (slate/navy).
 * Fallback silencieux en gradient CSS si WebGL indisponible.
 */
function ShaderBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return; // le gradient CSS sous le canvas sert de fallback

    const syncSize = () => {
      const w = canvas.clientWidth || 1280;
      const h = canvas.clientHeight || 720;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
    };
    syncSize();

    const vs = `attribute vec2 a_position;
varying vec2 v_texCoord;
void main(){ v_texCoord = a_position * 0.5 + 0.5; gl_Position = vec4(a_position,0.0,1.0); }`;

    const fs = `precision highp float;
varying vec2 v_texCoord;
uniform float u_time;
void main(){
  vec2 uv = v_texCoord;
  float t = u_time * 0.2;
  float noise = sin(uv.x*3.0+t)*cos(uv.y*2.0-t*0.5);
  noise += sin(uv.y*5.0-t*0.8)*cos(uv.x*4.0+t*0.3);
  noise = noise*0.5+0.5;
  // Theme clair : brume lavande/bleu tres pale
  vec3 color1 = vec3(0.96,0.97,1.00);
  vec3 color2 = vec3(0.90,0.92,0.99);
  vec3 color3 = vec3(0.85,0.88,0.98);
  vec3 col = mix(color1,color2,noise);
  col = mix(col,color3,sin(u_time*0.1)*0.2+0.2);
  // vignette douce (assombrit tres legerement les bords)
  float vignette = 1.0 - length(uv-0.5)*0.35;
  col *= vignette;
  gl_FragColor = vec4(col,1.0);
}`;

    const compile = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return s;
    };

    const prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, vs));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const pos = gl.getAttribLocation(prog, 'a_position');
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);
    const uTime = gl.getUniformLocation(prog, 'u_time');

    let raf;
    const render = (t) => {
      syncSize();
      gl.viewport(0, 0, canvas.width, canvas.height);
      if (uTime) gl.uniform1f(uTime, t * 0.001);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(raf);
      gl.deleteProgram(prog);
      gl.deleteBuffer(buf);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="absolute inset-0 h-full w-full opacity-60 pointer-events-none"
      style={{ display: 'block' }}
    />
  );
}

export default function LockScreen({
  user,
  onUnlock,
  onLogout,
  productName = 'FEDIS',
  madeBy = 'KIRASS GROUP',
}) {
  const { recheck } = useAuth();

  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(() => new Date());

  const inputRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Horloge live (mise a jour chaque seconde)
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const timeStr = now.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const dateStr = now.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  // Parallax leger de la carte au mouvement de la souris
  useEffect(() => {
    const el = cardRef.current;
    if (!el || window.matchMedia?.('(pointer: coarse)').matches) return;
    const onMove = (e) => {
      const x = (window.innerWidth / 2 - e.pageX) / 40;
      const y = (window.innerHeight / 2 - e.pageY) / 40;
      el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  function normalizeError(err) {
    const raw =
      err?.data?.error ||
      err?.response?.data?.error ||
      err?.error ||
      err?.message ||
      '';

    const code = String(raw).toLowerCase();

    if (code.includes('account_disabled')) return "Compte desactive par l'administrateur";
    if (code.includes('too_many_attempts')) return 'Trop de tentatives. Reessaie plus tard.';
    if (code.includes('unauthorized') || code.includes('invalid')) return 'Mot de passe incorrect';

    return 'Impossible de valider le mot de passe';
  }

  async function handleUnlock(e) {
    e?.preventDefault?.();
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      const ok = await recheck(password);

      if (ok) {
        setPassword('');
        onUnlock?.();
      } else {
        setError('Mot de passe incorrect');
      }
    } catch (err) {
      setError(normalizeError(err));
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  const username = user?.username || user?.email || user?.name || 'Utilisateur';
  const initials = useMemo(() => {
    const base = String(username || 'U')
      .trim()
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join('');
    return base || 'U';
  }, [username]);

  const canSubmit = !loading && !!password;

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Session verrouillee"
    >
      {/* Background : gradient clair (fallback) + shader WebGL anime */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-indigo-50" />
      <ShaderBackground />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(99,102,241,0.10),transparent_45%),radial-gradient(circle_at_80%_70%,rgba(59,130,246,0.08),transparent_50%)]" />

      {/* Horloge */}
      <div className="relative z-10 mb-8 select-none text-center">
        <div className="text-6xl sm:text-7xl font-extralight tracking-tight text-slate-800 tabular-nums leading-none drop-shadow-[0_2px_12px_rgba(99,102,241,0.15)]">
          {timeStr}
        </div>
        <div className="mt-2 text-sm font-medium capitalize text-slate-500">
          {dateStr}
        </div>
      </div>

      {/* Card */}
      <div ref={cardRef} className="relative w-full max-w-sm transition-transform duration-100 ease-out will-change-transform">
        <div className="rounded-3xl border border-slate-200/70 bg-white/70 shadow-xl shadow-indigo-500/10 backdrop-blur-xl overflow-hidden">
          <div className="h-1 w-full bg-gradient-to-r from-indigo-400 via-indigo-500 to-blue-400" />

          {/* Header */}
          <div className="px-8 pt-8 text-center">
            {/* Avatar + badge cadenas */}
            <div className="relative mx-auto h-20 w-20">
              <div className="h-20 w-20 rounded-3xl bg-gradient-to-br from-indigo-500 to-blue-500 border border-white/60 text-white flex items-center justify-center text-2xl font-bold shadow-lg ring-4 ring-indigo-500/10">
                {initials}
              </div>
              <div className="absolute -bottom-1 -right-1 rounded-full border border-slate-200 bg-white p-1.5 shadow-md">
                <Lock size={14} className="text-indigo-500" fill="currentColor" />
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-center gap-2 text-slate-400 text-xs font-semibold tracking-wide">
                {productName}
              </div>
              <div className="text-lg font-bold text-slate-800 mt-1">Session verrouillee</div>
              <div className="text-sm text-slate-500 mt-0.5">
                {username}
              </div>
            </div>
          </div>

          {/* Body */}
          <form onSubmit={handleUnlock} className="px-8 pb-8 pt-5 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-600">Mot de passe</label>
              <div className="mt-1 relative group">
                <Key size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                <input
                  ref={inputRef}
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="Mot de passe"
                  disabled={loading}
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-slate-200 bg-white/80 pl-10 pr-12 py-3 text-slate-800 placeholder:text-slate-400
                             focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  disabled={loading}
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className={[
                'w-full rounded-xl px-4 py-3 text-white font-semibold shadow-sm transition-all flex items-center justify-center gap-2 active:scale-[0.98]',
                canSubmit
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 hover:shadow-lg hover:shadow-indigo-500/30'
                  : 'bg-slate-200 cursor-not-allowed text-slate-400',
              ].join(' ')}
            >
              <span>{loading ? 'Verification...' : 'Deverrouiller'}</span>
              {!loading && <ArrowRight size={16} />}
            </button>

            <button
              type="button"
              onClick={() => !loading && onLogout?.()}
              disabled={loading}
              className="w-full rounded-xl px-4 py-3 font-semibold border border-slate-200 bg-white text-slate-600
                         hover:bg-slate-50 transition-colors disabled:opacity-60"
            >
              Changer d'utilisateur
            </button>

            <div className="pt-1 text-[11px] text-center text-slate-400 leading-relaxed">
              La session se verrouille automatiquement apres une periode d'inactivite.
            </div>
          </form>
        </div>

        {/* Stats decoratives */}
        <div className="mt-6 grid grid-cols-2 gap-4 px-2">
          <div className="flex flex-col gap-1 border-l border-slate-200 pl-3">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Systeme statut</span>
            <span className="flex items-center gap-1.5 text-[13px] font-medium text-slate-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Operationnel
            </span>
          </div>
          <div className="flex flex-col gap-1 border-l border-slate-200 pl-3">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Localisation</span>
            <span className="text-[13px] font-medium text-slate-600">NODE_ALPHA_01</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative mt-6 text-[11px] text-slate-400">
        Powered by <span className="font-semibold text-slate-500">{madeBy}</span>
      </div>
    </div>
  );
}
